import { AnimatePresence } from "motion/react";

import { FloatingWindowsProvider } from "src/base-components/FloatingWindow/FloatingWindowsProvider";
import { useResizableWidth } from "src/base-components/useResizableWidth";
import { useWorkspaceContext } from "src/router/routerContextHooks";
import { useCurrentUserId } from "src/store/AuthStore";
import { UnifiedCopilotChatSessionProvider } from "src/unifiedCopilot/chat/UnifiedCopilotChatSessionProvider";
import {
  GlobalContextualEntitiesContext,
  useWorkspaceGlobalContextualEntities,
} from "src/unifiedCopilot/context/globalContextualEntities";
import {
  UnifiedCopilotDisplayProvider,
  useUnifiedCopilotDisplay,
} from "src/unifiedCopilot/UnifiedCopilotDisplayProvider";
import { UnifiedCopilotLauncher } from "src/unifiedCopilot/UnifiedCopilotLauncher";
import { UnifiedCopilotSidebar } from "src/unifiedCopilot/UnifiedCopilotSidebar";
import { useIsUnifiedCopilotEnabled } from "src/unifiedCopilot/useIsUnifiedCopilotEnabled";

const UNIFIED_COPILOT_SIDEBAR_MIN_WIDTH = 320;
const UNIFIED_COPILOT_SIDEBAR_MAX_WIDTH = 640;
const UNIFIED_COPILOT_SIDEBAR_DEFAULT_WIDTH = 360;
const UNIFIED_COPILOT_SIDEBAR_WIDTH_STORAGE_KEY =
  "unifiedCopilotSidebarWidthV1";

type UnifiedCopilotProps = {
  children?: React.ReactNode;
};

type ScopedUnifiedCopilotProps = UnifiedCopilotProps & {
  userId: string;
  workspaceId: string;
};

const UnifiedCopilotWorkspaceLayout: React.FC<UnifiedCopilotProps> = ({
  children,
}) => {
  const { surface } = useUnifiedCopilotDisplay();
  const { width, isResizing, handleResizeStart } = useResizableWidth({
    side: "right",
    storageKey: UNIFIED_COPILOT_SIDEBAR_WIDTH_STORAGE_KEY,
    minWidth: UNIFIED_COPILOT_SIDEBAR_MIN_WIDTH,
    maxWidth: UNIFIED_COPILOT_SIDEBAR_MAX_WIDTH,
    defaultWidth: UNIFIED_COPILOT_SIDEBAR_DEFAULT_WIDTH,
  });

  return (
    <>
      <div
        className="flex min-h-0 w-full min-w-0 flex-1 overflow-clip"
        data-loc="unified-copilot-workspace-layout"
      >
        <div
          className="flex min-h-0 min-w-0 flex-1 flex-col overflow-clip"
          data-loc="unified-copilot-router-pane"
        >
          {children}
        </div>
        <AnimatePresence initial={false}>
          {surface === "sidebar" && (
            <UnifiedCopilotSidebar
              key="unified-copilot-sidebar"
              isResizing={isResizing}
              width={width}
              onResizeStart={handleResizeStart}
            />
          )}
        </AnimatePresence>
      </div>
      <FloatingWindowsProvider>
        <UnifiedCopilotLauncher />
      </FloatingWindowsProvider>
    </>
  );
};

const ScopedUnifiedCopilot: React.FC<ScopedUnifiedCopilotProps> = ({
  children,
  userId,
  workspaceId,
}) => {
  const globalContextualEntitiesContextValue =
    useWorkspaceGlobalContextualEntities();

  return (
    <GlobalContextualEntitiesContext
      value={globalContextualEntitiesContextValue}
    >
      <UnifiedCopilotChatSessionProvider
        userId={userId}
        workspaceId={workspaceId}
      >
        <UnifiedCopilotDisplayProvider
          userId={userId}
          workspaceId={workspaceId}
        >
          <UnifiedCopilotWorkspaceLayout>
            {children}
          </UnifiedCopilotWorkspaceLayout>
        </UnifiedCopilotDisplayProvider>
      </UnifiedCopilotChatSessionProvider>
    </GlobalContextualEntitiesContext>
  );
};

const EnabledUnifiedCopilot: React.FC<UnifiedCopilotProps> = ({ children }) => {
  const { workspace } = useWorkspaceContext();
  const userId = useCurrentUserId();

  // Already guaranteed by `useIsUnifiedCopilotEnabled`; narrows the type.
  if (userId === undefined) return children;

  return (
    <ScopedUnifiedCopilot
      key={`${userId}:${workspace.id}`}
      userId={userId}
      workspaceId={workspace.id}
    >
      {children}
    </ScopedUnifiedCopilot>
  );
};

// Must be mounted under a `CopilotContextProvider`
export const UnifiedCopilot: React.FC<UnifiedCopilotProps> = ({ children }) => {
  const { isEnabled } = useIsUnifiedCopilotEnabled();

  if (!isEnabled) return children;

  return <EnabledUnifiedCopilot>{children}</EnabledUnifiedCopilot>;
};
